import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Mail, RefreshCw } from "lucide-react";
import { api, API_BASE } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const Pricing = ({ prices, onSaved }) => {
  const [v, setV] = useState(prices);
  useEffect(() => { setV(prices); }, [prices]);
  const save = () => api.put("/admin/settings/report-pricing", { digital: Number(v.digital), printed: Number(v.printed), card: Number(v.card) })
    .then(() => { toast.success("Report prices saved"); onSaved(); }).catch((e) => toast.error(e?.response?.data?.detail || "Could not save"));
  return (
    <div className="flex flex-wrap items-end gap-2 text-sm">
      {["digital", "printed", "card"].map((k) => (
        <label key={k} className="flex flex-col text-xs text-slate-600">{k === "digital" ? "Digital brief $" : k === "printed" ? "Printed pilot $" : "Answer card $ (after 3 free/month)"}
          <Input data-testid={`report-price-${k}`} type="number" step="0.5" value={v?.[k] ?? ""} onChange={(e) => setV({ ...v, [k]: e.target.value })} className="h-9 w-28" />
        </label>
      ))}
      <Button data-testid="report-price-save" size="sm" onClick={save} className="h-9 rounded-full bg-emerald-700">Save prices</Button>
    </div>
  );
};

export default function AdminReports() {
  const [data, setData] = useState(null);
  const [sid, setSid] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/reports").then((r) => setData(r.data)).catch(() => setData({ reports: [], prices: {} }));
  useEffect(() => { load(); }, []);

  const act = (p, msg) => api.post(p).then((r) => { toast.success(msg + (r.data.email_status ? `: ${r.data.email_status}` : "")); load(); })
    .catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
  const preview = async () => {
    setBusy(true);
    try {
      await api.post("/admin/reports/preview", { session_id: sid.trim() });
      toast.success("Preview report is being generated");
      setTimeout(load, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start preview"); }
    setBusy(false);
  };

  return (
    <section data-testid="admin-reports" className="mt-6 rounded-2xl bg-white border border-emerald-100 p-4 space-y-4">
      <div className="flex items-center gap-2 font-display font-bold text-[#092C15]"><FileText size={18} /> Ask Dr. Verdant Reports <span className="text-xs font-normal text-amber-700">(private test copy)</span></div>
      {data && <Pricing prices={data.prices} onSaved={load} />}
      <div className="flex flex-wrap gap-2">
        <Input data-testid="report-preview-session" placeholder="Submission ID for a free preview report" value={sid} onChange={(e) => setSid(e.target.value)} className="h-9 max-w-sm" />
        <Button data-testid="report-preview-button" size="sm" disabled={!sid || busy} onClick={preview} className="h-9 rounded-full">
          {busy && <Loader2 size={14} className="mr-1 animate-spin" />}Generate preview</Button>
        <Button data-testid="report-refresh" size="sm" variant="outline" onClick={load} className="h-9 rounded-full"><RefreshCw size={14} /></Button>
      </div>
      <div className="space-y-2">
        {(data?.reports || []).map((r) => (
          <div key={r.id} data-testid={`admin-report-${r.id}`} className="rounded-xl border border-slate-200 p-3 text-sm flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <strong>{r.case_id}</strong><span>{r.package}{r.admin_comp ? " · admin" : ` · $${Number(r.amount).toFixed(2)}`}</span>
            <span className="text-xs rounded-full bg-slate-100 px-2 py-0.5">{r.status}</span>
            <span className="text-xs text-slate-500">email: {r.email_status || "—"}</span>
            <span className="text-xs text-slate-500">{r.customer_email}</span>
            <span className="text-xs text-slate-400 break-all">payment: {r.payment_id || "—"}</span>
            <div className="ml-auto flex flex-wrap gap-1.5">
              {r.status === "ready" && <a data-testid={`admin-report-open-${r.id}`} href={r.report_url} target="_blank" rel="noreferrer" className="text-xs font-bold text-emerald-700 underline">Report</a>}
              {r.email_status && <a data-testid={`admin-report-email-${r.id}`} href={`${API_BASE}/admin/reports/${r.id}/email-preview`} target="_blank" rel="noreferrer" className="text-xs font-bold text-sky-700 underline"><Mail size={12} className="inline" /> Email</a>}
              {r.status === "error" && <button data-testid={`admin-report-retry-${r.id}`} onClick={() => act(`/admin/reports/${r.id}/retry`, "Retrying")} className="text-xs font-bold text-amber-700 underline">Retry</button>}
              {r.status === "ready" && <button data-testid={`admin-report-resend-${r.id}`} onClick={() => act(`/admin/reports/${r.id}/resend-email`, "Email")} className="text-xs font-bold text-slate-700 underline">Resend email</button>}
            </div>
          </div>
        ))}
        {data && !data.reports.length && <p className="text-sm text-slate-500">No reports yet.</p>}
      </div>
    </section>
  );
}
